import { Controller } from "@hotwired/stimulus"
import { createConsumer } from "@rails/actioncable"

// Connects to data-controller="screen-designer"
export default class extends Controller {
  static targets = [
    "status", "generateBtn", "generateText", "requirements", "feedback",
    "preview", "previewContainer", "previewFrame", "codeView", "code",
    "progressBanner", "progressTitle", "progressMessage", "progressBar", "progressPercentage", "progressDetails",
    "versionSidebar", "requirementsForm", "feedbackForm", "contextTab", "chatTab",
    "notesTextarea", "notesCharCount", "notesSaveStatus", "notesErrorStatus",
    "chatMessages", "chatInput", "chatSendButton", "chatForm",
    "typingIndicator", "sessionStatus",
    "previewControls", "deviceToggles", "previewButton", "codeButton", "viewControls", "viewModeButtons"
  ]
  
  static values = {
    designId: String,
    appProjectId: String,
    outputFormat: { type: String, default: "html" }
  }

  connect() {
    console.log("Screen Designer controller connected")
    this.setupActionCableSubscription()
    this.currentDevice = "desktop"
    this.currentView = "preview"
    this.restoreActiveTab()
    this.restoreCollapseStates()

    // Initialize char count if notes textarea exists
    if (this.hasNotesTextareaTarget) {
      this.updateCharCount({ target: this.notesTextareaTarget })
    }

    // Add Ctrl+Enter shortcut for chat
    if (this.hasChatInputTarget) {
      this.chatInputTarget.addEventListener('keydown', (e) => {
        if (e.ctrlKey && e.key === 'Enter') {
          e.preventDefault()
          this.sendChatMessage(e)
        }
      })
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  // ActionCable setup
  setupActionCableSubscription() {
    if (!this.designIdValue) return
    
    this.consumer = createConsumer()
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: "ScreenDesignChannel",
        id: this.designIdValue
      },
      {
        connected: () => {
          console.log("Connected to ScreenDesignChannel")
          this.requestStatus()
        },
        
        disconnected: () => {
          console.log("Disconnected from ScreenDesignChannel")
        },
        
        received: (data) => {
          console.log("Received from ScreenDesignChannel:", data)
          this.handleChannelMessage(data)
        }
      }
    )
  }

  // Handle messages from ActionCable
  handleChannelMessage(data) {
    console.log("Handling channel message:", data.event)
    switch (data.event) {
      case 'generation_queued':
        console.log("Generation queued message received")
        // Banner is already shown when user clicks generate
        break
      case 'generation_started':
        this.showGenerationStarted()
        break
      case 'generation_progress':
        this.updateProgress(data.message)
        // Update progress with more details if available
        if (data.method) {
          const details = `Using ${data.method}`
          this.showProgressBanner(
            "Generating Design...",
            data.message,
            Math.min((this.progressBarTarget?.value || 10) + 15, 80),
            details
          )
        }
        break
      case 'generation_complete':
        this.showGenerationComplete(data.html)
        break
      case 'generation_failed':
        this.showGenerationFailed(data.error)
        break
      case 'refinement_queued':
        console.log("Refinement queued message received")
        // Banner is already shown when user clicks refine
        break
      case 'refinement_complete':
        this.showRefinementComplete(data.html)
        break
      case 'chat_typing':
        // Show typing indicator in chat AND progress banner
        this.showTypingIndicator()
        this.showProgressBanner(
          "Processing Request...",
          data.message || "Updating your design based on your message...",
          10
        )
        break
      case 'chat_response':
        // Hide typing indicator and progress banner, add AI message
        this.hideTypingIndicator()
        this.updateProgressBar(100)

        setTimeout(() => {
          this.hideProgressBanner()
          this.addMessageToChat('assistant', data.message, data.metadata || {})

          // Update preview if HTML was updated
          if (data.html) {
            this.updatePreview(data.html)
            this.showNotification("Design updated successfully!", "success")
          }
        }, 500) // Show 100% progress briefly before hiding
        break
      case 'chat_error':
        // Hide typing indicator, progress banner and show error
        this.hideTypingIndicator()
        this.hideProgressBanner()
        this.showNotification(data.error || 'Chat refinement failed', 'error')
        break
      case 'status_update':
        this.updateStatus(data)
        break
      case 'generation_cancelled':
        this.hideProgressBanner()
        this.resetGenerateButton()
        this.showNotification("Generation cancelled", "info")
        break
    }
  }

  // Generate new design
  generate(event) {
    event.preventDefault()

    // Find requirements input - could be from form or standalone textarea
    let requirements = ""
    if (this.hasRequirementsTarget) {
      requirements = this.requirementsTarget.value.trim()
    }

    // Check if this is a regeneration
    const isRegeneration = this.hasGenerateTextTarget &&
                          this.generateTextTarget.textContent.trim() === 'Regenerate'

    // For regeneration, always use default message (since Requirements section is hidden after first generation)
    if (isRegeneration) {
      // Confirm regeneration with user
      if (!confirm('Regenerate this screen design? This will create a new version.')) {
        return
      }
      requirements = "Regenerate this screen following the original specifications and sitemap details."
    } else {
      // Initial generation - requirements are optional
      // If blank, the backend will auto-generate from sitemap page data
      if (!requirements) {
        requirements = "" // Let backend handle auto-generation
      }
    }

    console.log("Starting generation with requirements:", requirements)

    // Show progress banner immediately
    this.showProgressBanner("Generating Design...", "Creating your screen design with AI. This may take a few moments.", 5)

    // Send generation request via ActionCable
    this.subscription.perform('generate', {
      requirements: requirements,
      design_style: this.getFormValue('design_style'),
      target_audience: this.getFormValue('target_audience')
    })

    // Update button state
    if (this.hasGenerateBtnTarget) {
      this.generateBtnTarget.disabled = true
      if (this.hasGenerateTextTarget) {
        this.generateTextTarget.textContent = "Generating..."
      }
    }
  }

  // Refine existing design
  refine(event) {
    event.preventDefault()
    
    const feedback = this.feedbackTarget.value.trim()
    if (!feedback) {
      alert("Please enter feedback before refining the design.")
      this.feedbackTarget.focus()
      return
    }

    console.log("Starting refinement with feedback:", feedback)

    // Show progress banner
    this.showProgressBanner("Refining Design...", "Applying your feedback to improve the design.")

    // Send refinement request via ActionCable
    this.subscription.perform('refine', {
      feedback: feedback
    })
    
    // Clear feedback input
    this.feedbackTarget.value = ""
  }

  // Tab switching
  switchTab(event) {
    event.preventDefault()
    const tabName = event.currentTarget.dataset.tab

    // Update tab active states
    this.element.querySelectorAll('.tab').forEach(tab => {
      tab.classList.remove('tab-active')
    })
    event.currentTarget.classList.add('tab-active')

    // Show/hide tab content
    if (tabName === 'context') {
      if (this.hasContextTabTarget) this.contextTabTarget.classList.remove('hidden')
      if (this.hasChatTabTarget) this.chatTabTarget.classList.add('hidden')
    } else if (tabName === 'chat') {
      if (this.hasContextTabTarget) this.contextTabTarget.classList.add('hidden')
      if (this.hasChatTabTarget) this.chatTabTarget.classList.remove('hidden')
    }

    // Persist to localStorage
    localStorage.setItem('screen_design_active_tab', tabName)
  }

  restoreActiveTab() {
    const activeTab = localStorage.getItem('screen_design_active_tab') || 'context'
    const tabElement = this.element.querySelector(`[data-tab="${activeTab}"]`)
    if (tabElement) {
      // Trigger click to activate the tab
      tabElement.click()
    }
  }

  // Collapse state management
  saveCollapseState(event) {
    const section = event.currentTarget.dataset.section
    const isCollapsed = !event.currentTarget.checked
    localStorage.setItem(`collapse_${section}`, isCollapsed)
  }

  restoreCollapseStates() {
    // Restore each section's collapsed state from localStorage
    // Note: In DaisyUI collapse, checked = expanded, unchecked = collapsed
    ['page-info', 'requirements', 'notes', 'debug', 'versions'].forEach(section => {
      const storedState = localStorage.getItem(`collapse_${section}`)
      const checkbox = this.element.querySelector(`[data-section="${section}"]`)

      if (checkbox && storedState !== null) {
        const isCollapsed = storedState === 'true'
        checkbox.checked = !isCollapsed // checked = expanded
      }
    })
  }

  // Notes functionality
  updateCharCount(event) {
    if (!this.hasNotesTextareaTarget || !this.hasNotesCharCountTarget) return

    const length = event.target.value.length
    this.notesCharCountTarget.textContent = `${length} / 2000`

    // Warn when approaching limit
    if (length > 1800) {
      this.notesCharCountTarget.classList.add('text-warning')
    } else {
      this.notesCharCountTarget.classList.remove('text-warning')
    }
  }

  async saveNotes(event) {
    if (!this.hasNotesTextareaTarget) return

    const notes = this.notesTextareaTarget.value

    // Hide previous status
    if (this.hasNotesSaveStatusTarget) {
      this.notesSaveStatusTarget.classList.add('hidden')
    }
    if (this.hasNotesErrorStatusTarget) {
      this.notesErrorStatusTarget.classList.add('hidden')
    }

    try {
      const response = await fetch(
        `/app_projects/${this.appProjectIdValue}/sprint_zero/screen_designs/${this.designIdValue}/update_notes`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken()
          },
          body: JSON.stringify({ llm_notes: notes })
        }
      )

      const data = await response.json()

      if (data.success) {
        // Show success status briefly
        if (this.hasNotesSaveStatusTarget) {
          this.notesSaveStatusTarget.classList.remove('hidden')
          setTimeout(() => {
            this.notesSaveStatusTarget.classList.add('hidden')
          }, 3000)
        }
      } else {
        throw new Error(data.error || 'Failed to save')
      }
    } catch (error) {
      console.error('Error saving notes:', error)
      if (this.hasNotesErrorStatusTarget) {
        this.notesErrorStatusTarget.classList.remove('hidden')
        setTimeout(() => {
          this.notesErrorStatusTarget.classList.add('hidden')
        }, 5000)
      }
    }
  }

  // Chat functionality
  async sendChatMessage(event) {
    event.preventDefault()

    if (!this.hasChatInputTarget) return

    const message = this.chatInputTarget.value.trim()

    if (!message) {
      alert('Please enter a message before sending.')
      this.chatInputTarget.focus()
      return
    }

    // Disable send button and input
    this.chatSendButtonTarget.disabled = true
    this.chatInputTarget.disabled = true

    try {
      // Add user message to UI immediately (optimistic update)
      this.addMessageToChat('user', message)

      // Clear input
      this.chatInputTarget.value = ''

      // Show typing indicator
      this.showTypingIndicator()

      // Send via AJAX (backend will handle ActionCable broadcast)
      const response = await fetch(
        `/app_projects/${this.appProjectIdValue}/sprint_zero/screen_designs/${this.designIdValue}/send_chat_message`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken()
          },
          body: JSON.stringify({ message: message })
        }
      )

      const data = await response.json()

      if (!data.success) {
        throw new Error(data.error || 'Failed to send message')
      }

      // Success - ActionCable will handle showing AI response

    } catch (error) {
      console.error('Error sending chat message:', error)

      // Remove optimistic user message on error
      this.removeLastUserMessage()

      // Show error to user
      this.showNotification(error.message || 'Failed to send message. Please try again.', 'error')

      // Hide typing indicator
      this.hideTypingIndicator()

      // Restore message to input so user can retry
      this.chatInputTarget.value = message

    } finally {
      // Re-enable input and button
      this.chatSendButtonTarget.disabled = false
      this.chatInputTarget.disabled = false
      this.chatInputTarget.focus()
    }
  }

  addMessageToChat(role, content, metadata = {}) {
    if (!this.hasChatMessagesTarget) return

    const messageDiv = document.createElement('div')
    messageDiv.className = role === 'user' ? 'chat chat-end' : 'chat chat-start'

    const bubbleClass = role === 'user' ? 'chat-bubble-primary' : 'chat-bubble-secondary'
    const timestamp = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

    messageDiv.innerHTML = `
      <div class="chat-bubble ${bubbleClass} text-sm">${this.escapeHtml(content)}</div>
      <div class="chat-footer opacity-50 text-xs">${timestamp}</div>
    `

    // Remove empty state if it exists
    const emptyState = this.chatMessagesTarget.querySelector('.text-center')
    if (emptyState) {
      emptyState.remove()
    }

    // Add message before typing indicator
    if (this.hasTypingIndicatorTarget) {
      this.chatMessagesTarget.insertBefore(messageDiv, this.typingIndicatorTarget)
    } else {
      this.chatMessagesTarget.appendChild(messageDiv)
    }

    this.scrollChatToBottom()
  }

  showTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.remove('hidden')
      this.scrollChatToBottom()
    }
  }

  hideTypingIndicator() {
    if (this.hasTypingIndicatorTarget) {
      this.typingIndicatorTarget.classList.add('hidden')
    }
  }

  removeLastUserMessage() {
    if (!this.hasChatMessagesTarget) return

    const userMessages = this.chatMessagesTarget.querySelectorAll('.chat-end')
    if (userMessages.length > 0) {
      userMessages[userMessages.length - 1].remove()
    }
  }

  scrollChatToBottom() {
    if (this.hasChatMessagesTarget) {
      this.chatMessagesTarget.scrollTop = this.chatMessagesTarget.scrollHeight
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Device preview controls
  setDevice(event) {
    const device = event.currentTarget.dataset.device
    console.log("Setting device preview to:", device)

    // Update button states
    this.element.querySelectorAll('[data-action*="setDevice"]').forEach(btn => {
      btn.classList.remove('btn-active')
    })
    event.currentTarget.classList.add('btn-active')

    // Update preview frame
    if (this.hasPreviewFrameTarget) {
      this.previewFrameTarget.dataset.device = device
      this.currentDevice = device
    }
  }

  // View mode controls
  setView(event) {
    const view = event.currentTarget.dataset.view
    console.log("=== setView called ===")
    console.log("View mode:", view)
    console.log("Has previewControls target:", this.hasPreviewControlsTarget)
    console.log("Has viewControls target:", this.hasViewControlsTarget)

    // Ensure toolbar is always visible
    if (this.hasPreviewControlsTarget) {
      this.previewControlsTarget.style.display = 'flex'
      console.log("Toolbar display set to flex")
    }

    // Update button states
    if (this.hasPreviewButtonTarget && this.hasCodeButtonTarget) {
      this.previewButtonTarget.classList.remove('btn-active')
      this.codeButtonTarget.classList.remove('btn-active')

      if (view === 'code') {
        this.codeButtonTarget.classList.add('btn-active')
      } else {
        this.previewButtonTarget.classList.add('btn-active')
      }
      console.log("Button states updated")
    }

    // Get the preview container
    const previewContainer = this.hasPreviewContainerTarget ? this.previewContainerTarget : null
    if (!previewContainer) {
      console.error("Preview container not found")
      return
    }

    // Find or get references to the views
    const previewFrame = previewContainer.querySelector('[data-screen-designer-target="previewFrame"]')
    const codeView = previewContainer.querySelector('[data-screen-designer-target="codeView"]')

    // Toggle between preview and code view
    if (view === 'code') {
      if (previewFrame) previewFrame.classList.add('hidden')
      if (codeView) codeView.classList.remove('hidden')
      // Hide device toggles in code view (they're not relevant)
      if (this.hasDeviceTogglesTarget) this.deviceTogglesTarget.classList.add('hidden')
    } else {
      if (previewFrame) previewFrame.classList.remove('hidden')
      if (codeView) codeView.classList.add('hidden')
      // Show device toggles in preview mode
      if (this.hasDeviceTogglesTarget) this.deviceTogglesTarget.classList.remove('hidden')
    }

    this.currentView = view
  }

  // Version management
  showVersionHistory() {
    console.log("Showing version history")
    this.versionSidebarTarget.classList.remove('hidden')
  }

  hideVersionHistory() {
    console.log("Hiding version history")
    this.versionSidebarTarget.classList.add('hidden')
  }

  revertToVersion(event) {
    const version = event.currentTarget.dataset.version
    if (!confirm(`Are you sure you want to revert to version ${version}? This will replace the current design.`)) {
      return
    }

    console.log("Reverting to version:", version)

    // Make HTTP request to revert
    fetch(`/app_projects/${this.appProjectIdValue}/sprint_zero/screen_designs/${this.designIdValue}/revert_version`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': this.getCSRFToken()
      },
      body: JSON.stringify({ version: version })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return response.json()
    })
    .then(data => {
      if (data.success) {
        location.reload() // Reload to show reverted version
      } else {
        alert(data.error || 'Failed to revert to version')
      }
    })
    .catch(error => {
      console.error('Error reverting version:', error)
      alert('Failed to revert to version')
    })
  }

  // Fullscreen toggle
  toggleFullscreen() {
    if (this.hasPreviewTarget) {
      if (this.previewTarget.requestFullscreen) {
        this.previewTarget.requestFullscreen()
      }
    }
  }

  // Cancel generation
  cancelGeneration() {
    console.log("Cancelling generation")

    if (this.subscription) {
      this.subscription.perform('cancel', {})
    }

    this.hideProgressBanner()
    this.resetGenerateButton()
  }

  // Status and progress updates
  requestStatus() {
    if (this.subscription) {
      this.subscription.perform('status', {})
    }
  }

  updateStatus(data) {
    console.log("Updating status:", data)
    
    if (this.hasStatusTarget) {
      if (data.last_generated_at) {
        const timeAgo = this.timeAgo(new Date(data.last_generated_at))
        this.statusTarget.textContent = `Last updated ${timeAgo} ago`
      } else {
        this.statusTarget.textContent = "Ready to generate"
      }
    }
    
    // Update generate button text
    if (this.hasGenerateTextTarget) {
      const buttonText = data.generation_count > 0 ? 'Regenerate' : 'Generate'
      this.generateTextTarget.textContent = buttonText
    }
  }

  // Progress banner states
  showProgressBanner(title, message, progress = 0, details = '') {
    console.log("Showing progress banner:", title, message, progress)

    if (this.hasProgressBannerTarget) {
      console.log("Progress banner target found")
      this.progressTitleTarget.textContent = title
      this.progressMessageTarget.textContent = message
      this.progressBannerTarget.classList.remove('hidden')
      this.updateProgressBar(progress)

      // Update details if provided
      if (this.hasProgressDetailsTarget && details) {
        this.progressDetailsTarget.textContent = details
      }
    } else {
      console.error("Progress banner target not found!")
      console.log("Available targets:", this.targets)
    }
  }

  hideProgressBanner() {
    if (this.hasProgressBannerTarget) {
      this.progressBannerTarget.classList.add('hidden')
      this.updateProgressBar(0)
    }
  }

  updateProgressBar(percentage) {
    if (this.hasProgressBarTarget) {
      // Use DaisyUI progress element (value attribute) instead of width style
      this.progressBarTarget.value = percentage

      // Update percentage display if target exists
      if (this.hasProgressPercentageTarget) {
        this.progressPercentageTarget.textContent = `${Math.round(percentage)}%`
      }
    }
  }

  // Generation event handlers
  showGenerationStarted() {
    console.log("Generation started")
    this.showProgressBanner("Generating Design...", "Creating your screen design with AI. This may take a few moments.", 10)
  }

  updateProgress(message) {
    console.log("Generation progress:", message)
    if (this.hasProgressMessageTarget) {
      this.progressMessageTarget.textContent = message
    }
    // Simulate progress increase
    if (this.hasProgressBarTarget) {
      const currentProgress = parseInt(this.progressBarTarget.value) || 10
      const newProgress = Math.min(currentProgress + 10, 90)
      this.updateProgressBar(newProgress)
    }
  }

  showGenerationComplete(html) {
    console.log("Generation complete")
    this.updateProgressBar(100)

    setTimeout(() => {
      this.hideProgressBanner()
      this.resetGenerateButton()

      if (html) {
        this.updatePreview(html)
        // Show success message
        this.showNotification("Design generated successfully!", "success")
        // Reload the page to show the feedback form and updated state
        // This ensures server-side rendered content is updated properly
        setTimeout(() => {
          location.reload()
        }, 1500)
      }
    }, 500) // Show 100% progress briefly before hiding
  }

  showGenerationFailed(error) {
    console.log("Generation failed:", error)
    this.hideProgressBanner()
    this.resetGenerateButton()
    this.showNotification(error || "Generation failed. Please try again.", "error")
  }

  showRefinementComplete(html) {
    console.log("Refinement complete")
    this.updateProgressBar(100)

    setTimeout(() => {
      this.hideProgressBanner()

      if (html) {
        this.updatePreview(html)
        this.showNotification("Design refined successfully!", "success")
      }
    }, 500) // Show 100% progress briefly before hiding
  }

  // Preview management
  updatePreview(html) {
    console.log("Updating preview with new HTML")
    
    // Check if we need to replace the empty state with the preview
    const previewContainer = this.hasPreviewContainerTarget ? this.previewContainerTarget : null
    
    if (!previewContainer) {
      console.error("Preview container not found")
      return
    }
    
    // Check if there's an empty state to hide
    const emptyState = previewContainer.querySelector('.text-center')
    if (emptyState) {
      console.log("Hiding empty state")
      emptyState.style.display = 'none'
    }
    
    // Check if preview iframe exists, if not create it
    let previewFrame = previewContainer.querySelector('[data-screen-designer-target="previewFrame"]')
    let iframe = previewContainer.querySelector('iframe[data-screen-designer-target="preview"]')
    
    if (!previewFrame) {
      console.log("Creating preview frame structure")
      
      // Create the preview frame structure
      previewFrame = document.createElement('div')
      previewFrame.className = 'preview-frame mx-auto bg-white rounded-lg shadow-lg overflow-hidden'
      previewFrame.setAttribute('data-screen-designer-target', 'previewFrame')
      previewFrame.setAttribute('data-device', 'desktop')
      
      iframe = document.createElement('iframe')
      iframe.className = 'w-full h-full border-0'
      iframe.style.height = '600px'
      iframe.setAttribute('data-screen-designer-target', 'preview')
      
      previewFrame.appendChild(iframe)
      
      // Create code view div
      const codeView = document.createElement('div')
      codeView.className = 'hidden'
      codeView.setAttribute('data-screen-designer-target', 'codeView')
      codeView.innerHTML = '<pre class="bg-gray-900 text-green-400 p-4 rounded-lg text-sm overflow-auto"><code data-screen-designer-target="code"></code></pre>'
      
      // Add both to container
      previewContainer.appendChild(previewFrame)
      previewContainer.appendChild(codeView)
    }
    
    // Update iframe source with the new HTML
    if (iframe) {
      // Use srcdoc for better HTML rendering
      iframe.srcdoc = html
      console.log("Updated iframe with new HTML using srcdoc")

      // Fallback for older browsers
      if (!iframe.srcdoc) {
        const blob = new Blob([html], { type: 'text/html' })
        iframe.src = URL.createObjectURL(blob)
        console.log("Updated iframe using blob URL fallback")
      }
    }

    // Update code view if it exists
    const codeElement = previewContainer.querySelector('[data-screen-designer-target="code"]')
    if (codeElement) {
      codeElement.textContent = html
      console.log("Updated code view")
    }
  }

  // Helper methods
  resetGenerateButton() {
    if (this.hasGenerateBtnTarget) {
      this.generateBtnTarget.disabled = false
    }
  }

  getFormValue(name) {
    const element = this.element.querySelector(`[name="${name}"]`)
    return element ? element.value : ""
  }

  getCSRFToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.getAttribute('content') : ''
  }

  timeAgo(date) {
    const now = new Date()
    const diffInSeconds = Math.floor((now - date) / 1000)
    
    if (diffInSeconds < 60) return `${diffInSeconds} seconds`
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours`
    return `${Math.floor(diffInSeconds / 86400)} days`
  }

  showNotification(message, type = "info") {
    // Create a simple toast notification
    const toast = document.createElement('div')
    toast.className = `alert alert-${type} fixed top-4 right-4 w-auto z-50 max-w-md`
    toast.innerHTML = `
      <div class="flex items-center gap-2">
        <i class="fas fa-${type === 'success' ? 'check-circle' : type === 'error' ? 'exclamation-circle' : 'info-circle'}"></i>
        <span>${message}</span>
      </div>
    `
    
    document.body.appendChild(toast)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast)
      }
    }, 5000)
  }
}